import { BEIGE_300, GRAY_400, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { ReactNode, FC } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import AddRoundedIcon from '@mui/icons-material/AddRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import { IconButton } from '@mui/material'
import { Label } from 'components/common/Label'
import { MUIDivider } from 'components/common/MUIDivider'
import { MUIInputFieldCounter } from 'components/common/MUIInputFieldCounter'
import { Nullable } from 'models/helpers'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

/**
 * @interface Props ResidentialUnitsBuilder properties
 */
interface Props {
  /** Number of floors per residential unit */
  units: number[]
  /** Number of locked units — units at or above this index show the remove action */
  minUnits: number
  /** Units at or above this index are charged as extra and show the extra-price tag — the current property type's own minimum. Kept separate from minUnits so a Multi-unit building's 3rd default unit stays removable without being tagged "extra". */
  extraUnitFromIndex: number
  /** Formatted rate-card price of each additional residential unit; null while the rate card loads */
  extraUnitPriceFormatted: Nullable<string>
  /** Caption rendered next to "Add extra unit" — the extra price or the switch-to-multi-unit note */
  addUnitCaption: ReactNode
  /** Rate for a floor beyond the unit count, shown beside the section label; null while the rate card loads */
  floorPriceHint: ReactNode
  /** Called with the new units array when unit floors change */
  onUnitsChange: (units: number[]) => void
  /** Called when the user adds a unit */
  onAddUnit: () => void
  /** Called with the unit index when the user removes a unit */
  onRemoveUnit: (unitIndex: number) => void
}

/**
 * @component
 * Residential-units configurator of the redesigned On-Site Area Report step (ISSUE-2187) —
 * used for Two-family house (Kat. 3) and Multi-unit building (Kat. 4). Each unit carries its
 * own number of floors; the totals feed the measurement payload.
 *
 * @example
 * <ResidentialUnitsBuilder units={units} minUnits={2} extraUnitFromIndex={3} extraUnitPriceFormatted="259,00 €" addUnitCaption={caption} onUnitsChange={handleUnitsChange} onAddUnit={handleAddUnit} onRemoveUnit={handleRemoveUnit} />
 */
export const ResidentialUnitsBuilder: FC<Props> = ({
  units,
  minUnits,
  extraUnitFromIndex,
  extraUnitPriceFormatted,
  addUnitCaption,
  floorPriceHint,
  onUnitsChange,
  onAddUnit,
  onRemoveUnit
}) => {
  const { t } = useTranslation(['purchase_flow'], { keyPrefix: 'measurement_step' })

  const handleUnitFloorsChange = (unitIndex: number, floors: number) => {
    onUnitsChange(units.map((unitFloors, index) => index === unitIndex ? floors : unitFloors))
  }

  return (
    <Stack gap=".6rem">

      {/* A unit covers one floor; only floors beyond the unit count are charged, so the rate belongs next to
          the section that owns those counters rather than on any single unit (ISSUE-2861) */}
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" flexWrap="wrap" gap=".8rem">

        <Label text={t('property_details.residential_units')} />

        {!!floorPriceHint &&
          <Typography variant="text-sm" color={GRAY_700} component="div">{floorPriceHint}</Typography>
        }

      </Stack>

      <Stack gap=".8rem">

        {units.map((unitFloors, index) => (
          <Stack key={`unit-${index}`} gap=".4rem" sx={{ border: `1px solid ${GRAY_400}`, borderRadius: '.8rem', padding: '1.2rem 1.6rem' }}>

            <Stack direction="row" justifyContent="space-between" alignItems="center">

              <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                {t('property_details.unit_title', { number: index + 1 })}
              </Typography>

              {index >= minUnits &&
                <Stack direction="row" gap=".4rem" alignItems="center">

                  {index >= extraUnitFromIndex && !!extraUnitPriceFormatted &&
                    <Typography variant="text-sm" color={GRAY_700}>
                      <Trans t={t} i18nKey="property_details.extra_price" values={{ price: extraUnitPriceFormatted }} components={{ strong: <strong /> }} />
                    </Typography>
                  }

                  <IconButton size="small" data-testid="remove-unit" aria-label={t('property_details.remove_unit', { number: index + 1 })} onClick={() => onRemoveUnit(index)}>
                    <DeleteOutlineRoundedIcon fontSize="small" />
                  </IconButton>

                </Stack>
              }

            </Stack>

            <MUIDivider margin={8} />

            <Stack gap=".6rem">

              <Label text={t('property_details.number_of_unit_floors')} />

              {/* 14rem matches the step's floors counter — at 10rem the +/- buttons overflow the box and clip the value to zero width */}
              <MUIInputFieldCounter
                minValue={1}
                counterValue={unitFloors}
                counterOnChange={(value) => handleUnitFloorsChange(index, value)}
                inputBoxWidth="14rem"
              />

            </Stack>

          </Stack>
        ))}

        <Stack
          alignItems="center"
          gap=".4rem"
          onClick={onAddUnit}
          sx={{ backgroundColor: BEIGE_300, borderRadius: '.8rem', padding: '1.6rem', marginTop: '.8rem', cursor: 'pointer' }}
        >

          <AddRoundedIcon fontSize="large" />

          <Typography variant="text-md" color={GRAY_900} component="div">

            <strong>{t('property_details.add_extra_unit')}</strong>{' '}

            <Typography component="span" variant="text-md" color={GRAY_700} fontWeight="medium">{addUnitCaption}</Typography>

          </Typography>

        </Stack>

      </Stack>

    </Stack>
  )
}
